import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Ship, FileCheck, PackageCheck, MessageSquare, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products, howItWorksSteps, galleryImages } from '../data/mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1619023283528-3a8589b0069e"
            alt="Agricultural Export"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B5E20]/90 via-[#1B5E20]/75 to-[#1B5E20]/60"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Exporting Premium African Food Products to Global Markets
            </h1>
            <p className="text-xl text-white/95 mb-8">
              Connecting Nigerian agricultural and food products with international wholesalers, distributors, and retailers. Building reliable supply channels for African food distribution.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/quote">
                <Button size="lg" className="bg-[#C8A951] hover:bg-[#B8994A] text-white font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all">
                  Request a Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/products">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#1B5E20] font-semibold px-8 py-6 text-lg transition-all">
                  View Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">How It Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our process for connecting international buyers with Nigerian food products
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {howItWorksSteps.map((step, index) => {
              const IconComponent = {
                MessageSquare,
                FileText,
                FileCheck,
                Ship,
                PackageCheck
              }[step.icon];

              return (
                <Card key={step.id} className="relative border-2 border-gray-200 hover:border-[#C8A951] transition-all hover:shadow-lg bg-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <div className="absolute -top-3 -right-3 w-8 h-8 bg-[#C8A951] rounded-full flex items-center justify-center font-bold text-white shadow-lg">
                      {step.id}
                    </div>
                    <h3 className="text-lg font-semibold text-[#333333] mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Strip */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Products We Export</h2>
            <p className="text-lg text-gray-600">Premium African agricultural products, carefully sourced and quality-controlled</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {products.map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-all border-0 group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <span className="text-xs bg-[#C8A951] text-white px-3 py-1 rounded-full font-semibold">
                      {product.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#333333] mb-2">{product.name}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <Link to={`/products/${product.slug}`}>
                    <Button variant="outline" className="w-full border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white transition-all">
                      View Details
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/products">
              <Button size="lg" className="bg-[#1B5E20] hover:bg-[#2E7D32] text-white font-semibold px-8">
                View All Products
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-[#F9F9F9] to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#333333] mb-6">Why Choose Folalabi Farms?</h2>
              <p className="text-lg text-gray-600 mb-8">
                We are in the business of connecting Nigerian agricultural and food products with international buyers through reliable sourcing, export coordination, and supplier relationships.
              </p>
              
              <div className="space-y-4">
                {[
                  'Wide range of African food products sourced from Nigeria',
                  'Support for wholesale and export supply discussions',
                  'Coordination of packaging and shipment preparation',
                  'Growing network of trusted Nigerian suppliers',
                  'Building long-term relationships with international buyers',
                  'Flexible approach to buyer requirements'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#1B5E20] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1678182451047-196f22a4143e"
                alt="Shipping Containers"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Our Operations</h2>
            <p className="text-lg text-gray-600">From farm to international markets</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.slice(0, 6).map((image) => (
              <div key={image.id} className="relative h-64 overflow-hidden rounded-lg group cursor-pointer">
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="font-semibold">{image.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1B5E20] to-[#2E7D32] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Discuss Your African Food Supply Needs?</h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            Contact us for wholesale enquiries, export coordination, or product sourcing discussions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/quote">
              <Button size="lg" className="bg-[#C8A951] hover:bg-[#B8994A] text-white font-semibold px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all">
                Request a Quote
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#1B5E20] font-semibold px-10 py-6 text-lg transition-all">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
